import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  Chip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import StorageIcon from '@mui/icons-material/Storage';
import { useDispatch } from 'react-redux';
import { updateScreenTitle } from '../../../../LayoutContainers/AppLayout/redux/AppSlice';
import { listStorageUsage, recomputeStorageUsage } from '../../../../services/reports';

const formatBytes = (bytes) => {
  const n = Number(bytes) || 0;
  if (n < 1024) return `${n} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let value = n / 1024;
  let i = 0;
  while (value >= 1024 && i < units.length - 1) {
    value /= 1024;
    i += 1;
  }
  return `${value.toFixed(value >= 100 ? 0 : value >= 10 ? 1 : 2)} ${units[i]}`;
};

const formatDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const StorageUsagePage = () => {
  const dispatch = useDispatch();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [recomputing, setRecomputing] = useState(false);
  const [error, setError] = useState(null);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(25);
  const [totalElements, setTotalElements] = useState(0);
  const [computedAt, setComputedAt] = useState(null);

  useEffect(() => {
    dispatch(updateScreenTitle('Storage Usage'));
    return () => dispatch(updateScreenTitle(''));
  }, [dispatch]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await listStorageUsage({ search: search || undefined, page, size });
      const content = data?.content || [];
      setRows(content);
      setTotalElements(data?.totalElements || 0);
      const latest = content.reduce((acc, r) => (r.computedAt && (!acc || r.computedAt > acc) ? r.computedAt : acc), null);
      setComputedAt(latest);
    } catch (err) {
      console.error('Error loading storage usage:', err);
      setError('Failed to load storage usage');
      setRows([]);
      setTotalElements(0);
    } finally {
      setLoading(false);
    }
  }, [search, page, size]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSearch = () => {
    setPage(0);
    setSearch(searchInput.trim());
  };

  const handleRecompute = async () => {
    setRecomputing(true);
    setError(null);
    try {
      await recomputeStorageUsage();
      await load();
    } catch (err) {
      console.error('Error recomputing storage:', err);
      setError('Failed to recompute storage');
    } finally {
      setRecomputing(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 } }}>
      <Card sx={{ borderRadius: 2 }}>
        <CardContent>
          {/* Header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <StorageIcon color="primary" />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Organization Storage Usage
              </Typography>
              <Chip label={`${totalElements} orgs`} size="small" color="primary" variant="outlined" />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              {computedAt && (
                <Typography variant="caption" color="text.secondary">
                  As of {formatDate(computedAt)}
                </Typography>
              )}
              <Button
                size="small"
                variant="outlined"
                color="secondary"
                startIcon={recomputing ? <CircularProgress size={16} /> : <RefreshIcon />}
                onClick={handleRecompute}
                disabled={recomputing}
                sx={{
                  whiteSpace: 'nowrap',
                  textTransform: 'none',
                  fontWeight: 600,
                  color: (theme) => (theme.palette.mode === 'dark' ? theme.palette.secondary.light : '#1f2937'),
                  borderColor: (theme) => (theme.palette.mode === 'dark' ? theme.palette.secondary.light : '#94a3b8'),
                  '&:hover': {
                    borderColor: (theme) => (theme.palette.mode === 'dark' ? theme.palette.secondary.main : '#64748b'),
                  },
                  '&.Mui-disabled': { color: '#64748b', borderColor: '#cbd5e1' },
                }}
              >
                {recomputing ? 'Recalculating…' : 'Recalculate'}
              </Button>
            </Box>
          </Box>

          {/* Search */}
          <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
            <TextField
              size="small"
              placeholder="Search by organization name…"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }}
              sx={{ flex: 1, minWidth: 240 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" color="disabled" />
                  </InputAdornment>
                ),
              }}
            />
            <Button variant="contained" size="small" onClick={handleSearch} sx={{ textTransform: 'none' }}>
              Search
            </Button>
            {search && (
              <Button
                variant="text"
                size="small"
                onClick={() => { setSearchInput(''); setSearch(''); setPage(0); }}
                sx={{ textTransform: 'none' }}
              >
                Clear
              </Button>
            )}
          </Box>

          {error && (
            <Typography variant="body2" color="error" sx={{ mb: 1.5 }}>{error}</Typography>
          )}

          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ '& th': { fontWeight: 700, bgcolor: 'action.hover' } }}>
                  <TableCell>Organization</TableCell>
                  <TableCell align="right">Storage Used</TableCell>
                  <TableCell>Updated</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={3} align="center" sx={{ py: 5 }}>
                      <CircularProgress size={24} />
                    </TableCell>
                  </TableRow>
                ) : rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3} align="center" sx={{ py: 5 }}>
                      <Typography variant="body2" color="text.secondary">
                        No storage data yet. It refreshes nightly — or click Recalculate.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((row) => (
                    <TableRow key={row.organisationId} hover>
                      <TableCell sx={{ fontWeight: 500 }}>{row.organisationName || '—'}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, color: 'primary.main' }}>
                        {formatBytes(row.storageBytes)}
                      </TableCell>
                      <TableCell sx={{ color: 'text.secondary', fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                        {formatDate(row.computedAt)}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={totalElements}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            rowsPerPage={size}
            onRowsPerPageChange={(e) => { setSize(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[10, 25, 50, 100]}
          />
        </CardContent>
      </Card>
    </Box>
  );
};

export default StorageUsagePage;
